import Grid from '@mui/material/Grid';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { StyledLinearProgress } from '../../ProgressBar';
import { useCallback, useEffect, useMemo } from 'react';
import { DOCTOR_CONFIRMATION_QUESTIONS } from '../questions';
import { Button } from '../../SubmitButton';
import useIsPolaris from 'src/interviews/hooks/useIsPolaris';
import { ErrorText } from '@buoyhealth/common.buoy-input';
import { getSubmitButtonVariant } from '../../SubmitButton/utils';
import usePolarisActor from 'src/hooks/usePolarisActor';
import useDoctorConfirmationContext from '../useDoctorConfirmationContext';
import { updateCustomer } from 'src/interviews/requests/public_site/stripe';
import useParentTransition from 'src/interviews/hooks/useParentTransition';
import useResults from 'src/interviews/hooks/useResults';
import { trackQuestionSubmit, trackSubmissionOfPayment } from '../analytics';
import { postEmailForDxConfirmation } from 'src/interviews/requests/site_api/dx_confirmation';

import type { IDoctorConfirmationForm } from '../useDoctorConfirmationContext';
import type { MiniInterviewType } from 'src/packages/polaris/types';

interface IDoctorConfirmationFormProps {
  onBack: () => void;
  interviewType: MiniInterviewType;
  interviewToken: string;
  isAlarmingDx: boolean;
}

const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred.';

const DoctorConfirmationForm = ({
  onBack,
  interviewType,
  interviewToken,
  isAlarmingDx,
}: IDoctorConfirmationFormProps) => {
  const actor = usePolarisActor();
  const { identifiers } = (actor as any)?.parent?.state?.context || {};
  const parentTransition = useParentTransition();
  const { token: resultsToken } = useResults() || {};

  const { customerId } = useDoctorConfirmationContext();

  const isPolaris = useIsPolaris();

  const stripe = useStripe();
  const elements = useElements();

  const doctorConfirmationQuestions = useMemo(
    () =>
      DOCTOR_CONFIRMATION_QUESTIONS.filter((question) =>
        question.alarmOnly ? isAlarmingDx : true,
      ),
    [isAlarmingDx],
  );

  const {
    currentQuestionIndex,
    setCurrentQuestionIndex,
    setIsError,
    setIsLoading,
    setErrorMessage,
    isError,
    errorMessage,
    isLoading,
    chargeAmount,
  } = useDoctorConfirmationContext();

  const { control, handleSubmit, watch } = useForm<IDoctorConfirmationForm>({
    defaultValues: {
      firstName: identifiers?.firstName || '',
      email: identifiers?.email || '',
      disclaimer: false,
      alarmDisclaimer: false,
    },
    reValidateMode: 'onBlur',
  });

  const [email, firstName] = watch(['email', 'firstName']);

  useEffect(() => {
    // Save user info to XState
    parentTransition({
      event: 'IDENTIFIERS_SUBMITTED',
      data: {
        identifiers: {
          firstName,
          email,
        },
      },
    });
  }, [email, firstName]);

  const { hideBackButton, Component, submitButtonText, shouldSubmit, isIntro } =
    doctorConfirmationQuestions[currentQuestionIndex];

  const goToNextQuestion = useCallback(() => {
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  }, [currentQuestionIndex]);

  const goBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else {
      onBack();
    }
  }, [currentQuestionIndex]);

  const submitStripePayment = useCallback(
    async (data: IDoctorConfirmationForm) => {
      trackSubmissionOfPayment({
        interviewToken,
        interviewType,
        chargeAmount,
      });

      if (stripe && elements) {
        const { error } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: typeof window !== 'undefined' ? window.location.href : '',
          },
          redirect: 'if_required',
        });

        // Associate user's info with the payment in Stripe
        if (customerId) {
          updateCustomer({
            customerId,
            email: data.email,
            name: data.firstName,
          });
        }

        if (error) {
          throw new Error(error.message || DEFAULT_ERROR_MESSAGE);
        }
      }
    },
    [chargeAmount, customerId, elements, interviewToken, interviewType, stripe],
  );

  const submitFormData: SubmitHandler<IDoctorConfirmationForm> = useCallback(
    async (data) => {
      if (!stripe || !elements || !resultsToken) {
        // Stripe.js hasn't yet loaded.
        return;
      }

      setIsLoading(true);

      const { firstName, email } = data;

      await submitStripePayment(data)
        .then(async () => {
          await postEmailForDxConfirmation({
            firstName,
            email,
            token: resultsToken,
          })
            .then(() => {
              // Save results token to XState to keep track of interviews that the user has requested confirmation for
              parentTransition({
                event: 'DOCTOR_CONFIRMATION_REQUEST_SUBMITTED',
                data: { resultsToken },
              });

              // show confirmation screen
              goToNextQuestion();
            })
            .catch(() => {
              // dx_confirmation submit fails. show full screen error message with restart option
              setIsError(true);
            });
        })
        .catch((error) => {
          // stripe error. show validation messages
          setErrorMessage(error?.message || DEFAULT_ERROR_MESSAGE);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      elements,
      goToNextQuestion,
      resultsToken,
      setErrorMessage,
      setIsError,
      setIsLoading,
      stripe,
      submitStripePayment,
    ],
  );

  const onSubmitClick: SubmitHandler<IDoctorConfirmationForm> = useCallback(
    (data) => {
      // alarming flows have one extra screen before the main questions, which will be logged as question # -1
      const questionNumber = isAlarmingDx ? currentQuestionIndex - 1 : currentQuestionIndex;

      trackQuestionSubmit({
        interviewToken,
        interviewType,
        questionNumber,
        chargeAmount,
      });

      const hasMoreQuestions = currentQuestionIndex < doctorConfirmationQuestions.length - 1;
      if (shouldSubmit) {
        submitFormData(data);
      } else if (hasMoreQuestions) {
        goToNextQuestion();
      } else {
        // All done. Take user back to results.
        onBack();
      }
    },
    [
      chargeAmount,
      currentQuestionIndex,
      doctorConfirmationQuestions.length,
      goToNextQuestion,
      interviewToken,
      interviewType,
      isAlarmingDx,
      onBack,
      shouldSubmit,
      submitFormData,
    ],
  );

  const containerClassNames = isPolaris
    ? 'bp-flex bp-flex-grow bp-flex-col bp-relative bp-overflow-hidden'
    : 'bp-flex bp-flex-grow bp-flex-col bp-relative bp-overflow-hidden bp-h-full';

  const footerContainerClassNames = isPolaris
    ? 'bp-z-10 bp-mb-[-1rem] bp-bg-white bp-p-4 bp-shadow-sm-up bp-absolute bp-bottom-0 bp-left-0 bp-w-full'
    : 'bp-z-10 bp-p-4 bp-shadow-sm-up';

  return (
    <div className={containerClassNames}>
      <div className="bp-flex-grow bp-overflow-scroll">
        <Grid
          container
          spacing={3}
          direction="column"
          padding={2}
          paddingTop={isPolaris ? 1 : 3}
          paddingBottom={isPolaris ? '90px' : 3}
        >
          {currentQuestionIndex !== 0 && (
            <Grid item>
              <StyledLinearProgress
                sx={{
                  height: '8px',
                  borderRadius: '5px',
                }}
                value={(currentQuestionIndex / doctorConfirmationQuestions.length) * 100}
                variant="determinate"
              />
            </Grid>
          )}
          <Grid item>
            <Component
              control={control}
              interviewToken={interviewToken}
              interviewType={interviewType}
            />
          </Grid>
        </Grid>
      </div>
      <div className={footerContainerClassNames}>
        <div className="bp-px-2">
          <ErrorText message={errorMessage || ''} show={isError} />
        </div>
        <div className="bp-flex bp-w-full bp-gap-2 bp-bg-white">
          <div className="bp-flex bp-flex-grow bp-items-center bp-justify-start">
            {isIntro && currentQuestionIndex === 0 ? (
              <div className="bp-flex bp-flex-col">
                <div className="bp-font-primary-main bp-font-bold bp-text-status-success">
                  ${chargeAmount}.00 one-time payment
                </div>
                <div className="bp-font-primary-smaller bp-mt-1 bp-text-gray-70">
                  No recurring charges. We promise, it's really as easy as it sounds.
                </div>
              </div>
            ) : (
              !hideBackButton && (
                <Button onClick={goBack} buttonVariant="back" disabled={isLoading}>
                  Back
                </Button>
              )
            )}
          </div>

          <div className="bp-flex bp-items-center bp-justify-end">
            <Button
              onClick={handleSubmit(onSubmitClick)}
              buttonVariant={getSubmitButtonVariant('Next')}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : submitButtonText?.() || 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorConfirmationForm;
